<template>
  <div data-app>
    <div
      role="alert"
      v-bind:class="{ show: errors.length }"
      class="alert fade alert-danger"
    >
      <div class="alert-text" v-for="(error, i) in errors" :key="i">
        {{ error }}
      </div>
    </div>

    <v-card>
      <v-card-title>BGP Sessions</v-card-title>
      <v-card-subtitle>
        Manage BGP sessions associated with your tunnels.
      </v-card-subtitle>
      <v-data-table
        v-model="selected"
        :loading="loading"
        :headers="headers"
        :items="bgpSessions"
        :search="search"
        :footer-props="{
          itemsPerPageOptions: [5, 10, 50, 100, 250, -1],
          showFirstLastPage: false,
          firstIcon: 'mdi-arrow-collapse-left',
          lastIcon: 'mdi-arrow-collapse-right',
          prevIcon: 'mdi-chevron-left',
          nextIcon: 'mdi-chevron-right',
        }"
        item-key="id"
        class="elevation-1"
      >
        <template v-slot:header.tunnel_id="{ header }">
          <span>{{ header.text }}</span>
        </template>
        <template v-slot:header.asn="{ header }">
          <span>{{ header.text }}</span>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon small class="ml-1" v-bind="attrs" v-on="on">
                mdi-help-circle-outline
              </v-icon>
            </template>
            <span>{{ headerHints.asn }}</span>
          </v-tooltip>
        </template>
        <template v-slot:header.local_asn="{ header }">
          <span>{{ header.text }}</span>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon small class="ml-1" v-bind="attrs" v-on="on">
                mdi-help-circle-outline
              </v-icon>
            </template>
            <span>{{ headerHints.local_asn }}</span>
          </v-tooltip>
        </template>
        <template v-slot:header.local="{ header }">
          <span>{{ header.text }}</span>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon small class="ml-1" v-bind="attrs" v-on="on">
                mdi-help-circle-outline
              </v-icon>
            </template>
            <span>{{ headerHints.local }}</span>
          </v-tooltip>
        </template>
        <template v-slot:header.neighbor="{ header }">
          <span>{{ header.text }}</span>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon small class="ml-1" v-bind="attrs" v-on="on">
                mdi-help-circle-outline
              </v-icon>
            </template>
            <span>{{ headerHints.neighbor }}</span>
          </v-tooltip>
        </template>
        <template v-slot:header.passive="{ header }">
          <span>{{ header.text }}</span>
        </template>
        <template v-slot:header.send_default_route="{ header }">
          <span>{{ header.text }}</span>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon small class="ml-1" v-bind="attrs" v-on="on">
                mdi-help-circle-outline
              </v-icon>
            </template>
            <span>{{ headerHints.send_default_route }}</span>
          </v-tooltip>
        </template>
        <template v-slot:top>
          <v-toolbar flat color="white">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Search"
              single-line
              hide-details
            ></v-text-field>
            <v-spacer></v-spacer>
            <v-dialog v-model="dialog" max-width="560px">
              <template v-slot:activator="{ on }">
                <v-btn color="primary" dark class="mb-2" v-on="on"
                  >New Session</v-btn
                >
              </template>
              <v-card>
                <v-card-title>
                  <span class="headline">{{ formTitle }}</span>
                </v-card-title>
                <v-card-text>
                  <v-form ref="bgpForm" lazy-validation>
                    <v-container>
                      <v-row>
                        <v-col cols="12" sm="12" md="12">
                          <v-select
                            v-model="editedItem.tunnel_id"
                            :items="tunnelOptions"
                            item-text="label"
                            item-value="id"
                            label="Tunnel"
                            :rules="[v => !!v || 'Tunnel is required']"
                          />
                        </v-col>
                        <v-col cols="12" sm="6" md="6">
                          <v-select
                            v-model.number="editedItem.asn"
                            :items="asnOptions"
                            item-text="label"
                            item-value="value"
                            label="ASN"
                            :rules="asnRules"
                          ></v-select>
                        </v-col>
                        <v-col cols="12" sm="6" md="6">
                          <v-select
                            v-model.number="editedItem.ip_version"
                            :items="ipVersionOptions"
                            label="IP Version"
                            :rules="[v => !!v || 'IP version is required']"
                          />
                        </v-col>
                        <v-col cols="12" sm="12" md="12">
                          <v-checkbox
                            v-model="editedItem.send_default_route"
                            label="Send Default Route"
                          ></v-checkbox>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-form>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="blue darken-1" text @click="close"
                    >Cancel</v-btn
                  >
                  <v-btn color="blue darken-1" text @click="save">Save</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar>
        </template>
        <template v-slot:item.local="{ item }">
          {{ item.local || "—" }}
        </template>
        <template v-slot:item.asn="{ item }">
          {{ formatAsn(item.asn) }}
        </template>
        <template v-slot:item.local_asn="{ item }">
          {{ formatAsn(item.local_asn) }}
        </template>
        <template v-slot:item.neighbor="{ item }">
          {{ item.neighbor || "—" }}
        </template>
        <template v-slot:item.send_default_route="{ item }">
          {{ item.send_default_route ? "Yes" : "No" }}
        </template>
        <template v-slot:item.passive="{ item }">
          {{ item.passive ? "Yes" : "No" }}
        </template>
        <template v-slot:item.actions="{ item }">
          <v-icon small class="mr-2" @click="editItem(item)">mdi-pencil</v-icon>
          <v-icon small @click="deleteItem(item)">mdi-delete</v-icon>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import api from "@/common/api.service";
import Errors from "@/common/errors";

export default {
  mounted() {
    this.loadData();
  },
  data: () => ({
    selected: [],
    headers: [
      { text: "ID", align: "left", sortable: false, value: "id" },
      { text: "Tunnel", value: "tunnel_id" },
      { text: "Peer ASN", value: "asn" },
      { text: "Local ASN", value: "local_asn" },
      { text: "Local IP", value: "local" },
      { text: "Neighbor IP", value: "neighbor" },
      { text: "Passive", value: "passive" },
      { text: "Default Route", value: "send_default_route" },
      { text: "Actions", value: "actions", sortable: false },
    ],
    bgpSessions: [],
    customerTunnels: [],
    asns: [],
    errors: [],
    loading: true,
    dialog: false,
    editedIndex: -1,
    editedItem: {
      id: "",
      tunnel_id: "",
      asn: null,
      send_default_route: false,
      ip_version: null,
    },
    defaultItem: {
      id: "",
      tunnel_id: "",
      asn: null,
      send_default_route: false,
      ip_version: null,
    },
    search: "",
    ipVersionOptions: [4, 6],
    headerHints: {
      asn: "The ASN of your BGP peer.",
      local_asn: "The ASN of Path's BGP peer.",
      local: "The address of Path's BGP peer.",
      neighbor: "The address of your BGP peer.",
      send_default_route:
        "Whether or not a default route will be sent over the BGP session.",
    },
    asnRules: [
      (v) => !!v || "Peer ASN is required",
    ],
  }),
  computed: {
    formTitle() {
      return this.editedIndex === -1 ? "New Session" : "Edit Session";
    },
    tunnelOptions() {
      return this.customerTunnels.map((tunnel) => ({
        id: tunnel.id,
        label: tunnel.user_comment
          ? `${tunnel.user_comment} (${tunnel.id})`
          : `${tunnel.id} (${tunnel.local} -> ${tunnel.remote})`,
      }));
    },
    asnOptions() {
      return this.asns
        .map((item) => Number(item.asn))
        .filter((asn) => Number.isInteger(asn) && asn > 0)
        .map((asn) => ({
          label: `AS${asn}`,
          value: asn,
        }));
    },
  },
  watch: {
    dialog(val) {
      val || this.close();
    },
  },
  methods: {
    loadData() {
      this.loading = true;
      Promise.all([
        api.query("/v2/bgp"),
        api.query("customer_tunnels"),
        api.query("/v2/asns"),
      ])
        .then(([bgpResponse, tunnelsResponse, asnsResponse]) => {
          this.bgpSessions = bgpResponse.data.bgp_sessions;
          this.customerTunnels = tunnelsResponse.data.customer_tunnels;
          this.asns = asnsResponse.data.asns || [];
        })
        .catch((error) => {
          this.errors.push(...Errors.extractErrorMessage(error));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    editItem(item) {
      this.editedIndex = this.bgpSessions.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialog = true;
    },
    close() {
      this.dialog = false;
      setTimeout(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      }, 300);
    },
    save() {
      if (!this.$refs.bgpForm.validate()) return;

      if (this.editedIndex > -1) {
        api
          .patch(`/v2/bgp/${this.editedItem.id}`, {
            tunnel_id: this.editedItem.tunnel_id,
            asn: this.editedItem.asn,
            send_default_route: this.editedItem.send_default_route,
            ip_version: this.editedItem.ip_version,
          })
          .then((response) => {
            Object.assign(
              this.bgpSessions.find((session) => session.id === response.data.id),
              response.data
            );
          })
          .catch((error) => this.errors.push(...Errors.extractErrorMessage(error)));
      } else {
        api
          .post("/v2/bgp", {
            tunnel_id: this.editedItem.tunnel_id,
            asn: this.editedItem.asn,
            send_default_route: this.editedItem.send_default_route,
            ip_version: this.editedItem.ip_version,
          })
          .then((response) => {
            this.bgpSessions.unshift(response.data);
          })
          .catch((error) => this.errors.push(...Errors.extractErrorMessage(error)));
      }
      this.close();
    },
    deleteItem(item) {
      if (!confirm("Are you sure you want to delete this BGP session?")) return;

      api
        .delete(`/v2/bgp/${item.id}`)
        .then(() => {
          const index = this.bgpSessions.indexOf(item);
          if (index > -1) {
            this.bgpSessions.splice(index, 1);
          }
        })
        .catch((error) => this.errors.push(...Errors.extractErrorMessage(error)));
    },
    formatAsn(asn) {
      return `AS${asn}`;
    },
  },
};
</script>
